/**
 * Russian UI copy (default locale).
 *
 * Text that is **not** defined here — rendered as returned by the backend:
 *
 * - `UserProfile`: firstName, lastName, username, email
 *   (GET /api/v1/users/me)
 * - `PaymentPackage.label`, amount, currency
 *   (GET /api/v1/payments/packages; price formatted client-side)
 * - `ReadingDetail` / `ReadingListItem`: userQuestion, interpretation,
 *   cards[].name, cards[].description
 * - `ReadingListItem.layoutType`, `ReadingListItem.type` (may be English enums)
 * - `JobStatusFailed.errorMessage` (reading job polling)
 * - `ErrorEnvelope.error.message` (preferred over local fallbacks in toasts)
 * - `PublicConfig.supportTgUsername` (Telegram handle, not a label)
 */

function pluralRu(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.trunc(n));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export const ru = {
  app: {
    title: 'Таро онлайн — Tarobot',
  },

  common: {
    back: 'Назад',
    loading: 'Загрузка…',
    loadingMore: 'Загружаем…',
    saving: 'Сохраняем…',
    sending: 'Отправляем…',
    sent: 'Отправлено',
    cancel: 'Отмена',
    save: 'Сохранить',
    buy: 'Купить',
    emailLabel: 'EMAIL',
    emailPlaceholder: 'your@email.com',
    passwordLabel: 'ПАРОЛЬ',
    passwordPlaceholder: '••••••••',
    passwordRequired: 'Введите пароль.',
    passwordMin8: 'Минимум 8 символов.',
    passwordMax128: 'Не более 128 символов.',
    passwordMin8Placeholder: 'Минимум 8 символов',
    emailInvalid: 'Укажите корректный email.',
    emailInternationalUnsupported: 'Используйте латинские символы до знака @.',
    passwordsMustMatch: 'Пароли должны совпадать.',
    passwordsDoNotMatch: 'Пароли не совпадают.',
    backToLogin: '← Вернуться на страницу входа',
    userFallback: 'Пользователь',
    unitPcs: 'шт',
    spreadsCount: (n: number) =>
      `${n} ${pluralRu(n, 'расклад', 'расклада', 'раскладов')}`,
  },

  dates: {
    monthsFull: [
      'января',
      'февраля',
      'марта',
      'апреля',
      'мая',
      'июня',
      'июля',
      'августа',
      'сентября',
      'октября',
      'ноября',
      'декабря',
    ] as const,
    monthsShort: [
      'янв.',
      'фев.',
      'мар.',
      'апр.',
      'мая',
      'июн.',
      'июл.',
      'авг.',
      'сен.',
      'окт.',
      'ноя.',
      'дек.',
    ] as const,
    spreadCompleted: (
      day: number,
      month: string,
      year: number,
      h: string,
      m: string,
    ) => `${day} ${month} ${year} г. в ${h}:${m}`,
    historyItem: (
      day: number,
      month: string,
      year: number,
      h: string,
      m: string,
    ) => `${day} ${month} ${year}, ${h}:${m}`,
  },

  nav: {
    ariaMain: 'Навигация',
    ariaMenu: 'Меню',
    ariaAccount: 'Аккаунт',
    menu: 'Меню',
    account: 'Аккаунт',
    home: 'Главная',
    newSpread: 'Новый расклад',
    spreadShort: 'Расклад',
    result: 'Результат',
    profile: 'Профиль',
  },

  api: {
    unknownError: 'Неизвестная ошибка. Попробуйте ещё раз.',
    fallback: 'Что-то пошло не так. Попробуйте ещё раз.',
    status: {
      400: 'Проверьте введённые данные.',
      401: 'Сессия истекла. Войдите снова.',
      402: 'Недостаточно раскладов. Пополните баланс.',
      403: 'Нет доступа.',
      404: 'Ничего не найдено.',
      409: 'Такой пользователь уже существует.',
      429: 'Слишком много запросов. Попробуйте позже.',
      500: 'Ошибка сервера. Повторите попытку.',
    },
    codes: {
      PASSWORD_TOO_SHORT: 'Пароль должен содержать не менее 8 символов.',
      PASSWORD_TOO_LONG: 'Пароль должен содержать не более 128 символов.',
      PASSWORD_COMPROMISED:
        'Этот пароль слишком распространён. Придумайте другой.',
      INTERNATIONAL_LOCAL_PART_UNSUPPORTED:
        'Используйте латинские символы до знака @.',
      NETWORK_ERROR:
        'Нет соединения с сервером. Проверьте интернет и попробуйте снова.',
      CSRF_INVALID: 'Сессия устарела. Обновите страницу и попробуйте снова.',
    },
  },

  auth: {
    tabsAria: 'Вход и регистрация',
    login: 'Войти',
    register: 'Зарегистрироваться',
    loginSubmitting: 'Входим…',
    registerSubmitting: 'Создаём аккаунт…',
    orEmail: 'или по email',
    loginTitle: 'Войти в аккаунт',
    loginMobileSub: 'Выберите способ входа или используйте email.',
    loginHeroTitle: 'Войти в Таробот',
    brandName: 'Таро онлайн',
    loginHeroSub: 'Чтобы хранить историю раскладов\nи пополнять баланс',
    loginBullet1: 'Загрузите фото своего расклада — бот расшифрует за минуту',
    loginBullet2: 'Задавайте вопросы раскладу — получайте точные ответы',
    loginBullet3: 'История раскладов сохраняется в вашем профиле',
    wrongCredentials: 'Неверный email или пароль.',
    emailNotVerified: 'Email не подтверждён.',
    enterCode: 'Ввести код',
    resendEmail: 'Выслать письмо снова',
    resendAgain: 'Выслать снова',
    resendAgainShort: 'Отправить снова',
    resendFailed: 'Не удалось отправить письмо.',
    registerFailed: 'Не удалось зарегистрироваться.',
    googleLoginFailed: 'Не удалось войти через Google.',
    googleLogin: 'Войти через Google',
    googleRegister: 'Регистрация через Google',
    forgotPassword: 'Забыли пароль?',
    confirmPasswordLabel: 'ПОВТОР ПАРОЛЯ',
    checkEmailTitle: 'Проверьте почту',
    checkEmailBody: 'Мы отправили письмо на',
    checkEmailHint: 'Введите шестизначный код из письма, чтобы войти.',
    emailSent: 'Письмо отправлено',
    verify: {
      verifying: 'Проверяем ссылку…',
      codeTitle: 'Подтвердите email',
      codeSub: 'Введите шестизначный код из письма. Код действует 10 минут.',
      codeLabel: 'Код подтверждения',
      codeLength: 'Введите все 6 цифр кода.',
      invalidCode: 'Код недействителен или устарел.',
      submit: 'Подтвердить email',
      submitting: 'Проверяем…',
      successTitle: 'Email подтверждён!',
      redirecting: 'Перенаправляем вас…',
      invalidLinkTitle: 'Ссылка недействительна',
      invalidLinkSub:
        'Возможно, ссылка устарела или уже была использована. Запросите новую ссылку:',
      resendOk: 'Письмо отправлено — проверьте почту.',
      badTokenTitle: 'Ссылка некорректна',
      badTokenSub: 'Используйте ссылку из письма о подтверждении.',
    },
    forgot: {
      title: 'Сброс пароля',
      sub: 'Введите email вашего аккаунта, и мы пришлём шестизначный код.',
      submit: 'Получить код',
      sendFailed: 'Не удалось отправить письмо. Попробуйте позже.',
    },
    reset: {
      badTokenTitle: 'Ссылка некорректна',
      badTokenSub: 'Используйте ссылку из письма о сбросе пароля.',
      requestNewLink: 'Запросить новую ссылку',
      requestNewCode: 'Получить новый код',
      successTitle: 'Пароль изменён!',
      successSub: 'Перенаправляем вас на страницу входа…',
      successToast: 'Пароль успешно изменён!',
      invalidLink: 'Ссылка недействительна или устарела.',
      invalidCode: 'Код недействителен или устарел.',
      title: 'Новый пароль',
      sub: 'Придумайте новый пароль для вашего аккаунта.',
      codeSub:
        'Введите код из письма и придумайте новый пароль. Код действует 10 минут.',
      codeLabel: 'Код для сброса пароля',
      newPasswordPlaceholder: 'Новый пароль (мин. 8 символов)',
      confirmPlaceholder: 'Повторите пароль',
      submit: 'Сохранить пароль',
    },
    changePassword: {
      title: 'Изменить пароль',
      currentPlaceholder: 'Текущий пароль',
      newPlaceholder: 'Новый пароль (мин. 8 символов)',
      confirmPlaceholder: 'Повторите новый пароль',
      successToast: 'Пароль изменён. Войдите снова.',
      failed: 'Не удалось изменить пароль.',
    },
    logout: 'Выйти',
    logoutSubmitting: 'Выходим…',
  },

  home: {
    pickOption: 'Выберите вариант:',
    options: {
      photoReading: {
        title: 'Расшифровка расклада по фото Таро',
        description:
          'Сделайте расклад Таро на своих картах и пришлите фото расклада. Можете задать вопрос',
      },
      personPhoto: {
        title: 'Генерация Таро по фото человека',
        description:
          'Сгенерируйте расклад по фото человека. Можете задать вопрос',
      },
      generate: {
        title: 'Генерация расклада Таро',
        description:
          'Сгенерируйте расклад. Можете загадать ваш вопрос или написать его в поле',
      },
    },
    stats: {
      spreadsDoneValue: '50 000+',
      spreadsDone: 'раскладов сделано',
      avgRatingValue: '4.9',
      avgRating: 'средняя оценка',
      avgResponseTimeValue: '1 мин',
      avgResponseTime: 'среднее время ответа',
    },
  },

  spread: {
    title: 'Новый расклад',
    selectTitle: 'Выбор расклада',
    modeAria: 'Тип расклада',
    modes: {
      photoReading: 'Расшифровка по фото',
      personPhoto: 'По фото человека',
      generate: 'Генерация расклада',
    },
    layoutSection: 'Тип Расклада:',
    types: {
      autoPhoto: {
        name: 'Автораспознавание',
        description: 'Распознаёт любой расклад по фото',
      },
      classic3: {
        name: '3 карты',
        description: 'Прошлое, настоящее, будущее',
        personPhotoDescription: 'Генерация расклада из 3-х карт',
      },
      cross4: {
        name: 'Крест',
        description: 'Прошлое, настоящее, совет, будущее',
      },
      horseshoe: {
        name: 'Подкова',
        description: 'Прошлое, настоящее, скрытое, совет, итог',
      },
    },
    questionLabel: 'Вопрос',
    questionOptional: '(не обязательно)',
    questionPlaceholder: 'Задайте вопрос к раскладу...',
    submit: 'Получить результат',
    createFailed: 'Не удалось создать расклад.',
    rateLimited: 'Слишком часто. Повторите через минуту.',
    insufficientBalance: 'Недостаточно раскладов. Пополните баланс.',
    aside: {
      title: 'Как это работает',
      intro:
        'Выберите тип расклада, загрузите фото (если нужно) и нажмите «Получить результат».',
      tips: {
        photo: {
          lead: 'Для лучшего результата',
          body: ' сфотографируйте карты при хорошем освещении, без теней и размытия.',
        },
        question: {
          lead: 'Вопрос к раскладу',
          body: ' поможет сделать расшифровку точнее и персональнее.',
        },
        time: {
          lead: 'Время ответа',
          body: ' — обычно 1–3 минуты. Результат сохраняется в вашем профиле.',
        },
      },
    },
  },

  photoUpload: {
    label: 'Фото',
    helper: 'PNG, JPG до 20 МБ',
    tapToUpload: 'Нажмите, чтобы загрузить',
    removeAria: 'Удалить фото',
    tooLarge: 'Файл слишком большой. Максимум 20 МБ.',
  },

  loading: {
    title: 'Карты читают судьбу',
    // Rotate every 10 s; the last one is held for the final stretch.
    stages: [
      'Загружаем ваш расклад',
      'Всматриваемся в карты',
      'Анализируем расклад',
      'Ищем связи между картами',
      'Толкуем значения карт',
      'Формируем результат',
    ],
    // Shown once a reading runs past the average duration.
    overtimeStages: [
      'Ваш расклад сложнее обычного',
      'Дописываем толкование',
      'Почти готово',
    ],
    progressAria: 'Прогресс расклада',
    // Rounded up (overestimating beats underestimating): 10 s steps under a
    // minute, half-minute steps above.
    remaining: (ms: number) => {
      const seconds = Math.max(10, Math.ceil(ms / 10_000) * 10);
      if (seconds < 60) return `осталось ~${seconds} сек`;
      const minutes = String(Math.ceil(seconds / 30) / 2).replace('.', ',');
      return `осталось ~${minutes} мин`;
    },
    done: 'готово',
    jobFailed: 'Не удалось выполнить расклад.',
    jobStatusFailed:
      'Не удалось получить статус расклада. Готовый расклад появится в истории.',
    resultOpenFailed: 'Расклад готов, но не удалось открыть результат.',
  },

  result: {
    title: 'Результат расклада',
    backToSpreads: 'К раскладам',
    notSelectedTitle: 'Расклад не выбран',
    notSelectedSub: 'Откройте расклад из истории в профиле или создайте новый.',
    loadFailed: 'Не удалось загрузить расклад',
    toHome: 'На главную',
    yourQuestion: 'Ваш вопрос:',
    interpretation: 'Интерпретация:',
    descriptionHeading: 'Описание твоего расклада:',
    cardsHeading: 'Карты расклада:',
    interactionHeading: 'Взаимодействие карт:',
    forecastHeading: 'Прогноз:',
    ratingPrompt: 'Оцени расклад',
    ratings: {
      good: 'Отлично',
      neutral: 'Нейтрально',
      bad: 'Не понравилось',
    },
    ratingThanks: 'Спасибо за оценку!',
    ratingFailed: 'Не удалось сохранить оценку.',
    aside: {
      balanceTitle: 'Ваш баланс',
      spreadsLeft: (n: number) => `Раскладов осталось ${n} шт`,
      topUp: 'Пополнить баланс',
      savedNote:
        'Результат сохранён в вашем профиле — вы всегда можете вернуться к нему позже.',
    },
  },

  profile: {
    balanceTitle: 'Баланс раскладов',
    balanceSub: 'раскладов осталось',
    buySpreads: 'Купить расклады',
    bestDeal: 'ВЫГОДНО',
    packageFallback: 'Пакет',
    tariffsLoading: 'Загрузка тарифов…',
    tariffsError: 'Не удалось загрузить тарифы. Попробуйте позже.',
    payRedirecting: 'Переход к оплате…',
    buyPackage: (label: string, price: string) =>
      `Купить: ${label}\u00A0— ${price}`,
    unlimitedSub: 'Без автоматических списаний',
    selectedPackage: 'Выбранный пакет',
    support: {
      title: 'Поддержка',
      prompt: 'Остались вопросы? Напишите нам:',
      telegram: 'Написать в Telegram',
      email: 'Написать на email',
      emailAddress: 'support@tarobot.online',
    },
    history: {
      title: 'История раскладов',
      loadFailed: 'Не удалось загрузить историю.',
      empty: 'У вас ещё нет раскладов.',
      showMore: 'Показать ещё',
      prev: 'Назад',
      next: 'Далее',
      openReading: 'Открыть расклад',
      readingTypes: {
        PHOTO_READING: 'Расшифровка по фото',
        GENERATE_RANDOM: 'Генерация расклада',
        GENERATE_BY_PHOTO: 'По фото человека',
        DAILY_CARD: 'Карта дня',
        photo_reading: 'Расшифровка по фото',
        generated_reading: 'Генерация расклада',
        generated_by_person_photo: 'По фото человека',
        daily_card: 'Карта дня',
      },
      layouts: {
        THREE_CARDS: '3 карты',
        THREE_CARD: '3 карты',
        CROSS: 'Крест',
        CELTIC_CROSS: 'Кельтский крест',
        HORSE_SHOE: 'Подкова',
        CUSTOM: 'Автораспознавание',
      },
    },
  },
} as const;

export type RuStrings = typeof ru;
